import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './ProfileTipsModal.css';

type Tip = {
  title: string;
  body: string;
  cta?: { label: string; path: string };
};

const PROFILE_TIPS: Tip[] = [
  {
    title: 'Answer 4–6 screening prompts on video',
    body: 'Recruiters use SkipScreen to pre-screen before live calls. Record concise 30–90 second answers to common questions — your public profile link shows these first.',
    cta: { label: 'Go to Prompts', path: '/prompts' },
  },
  {
    title: 'Make each prompt “Complete”',
    body: 'Strong responses include a video, a short written note for context, and an optional supporting document (resume section, case study, or certificate). Incomplete prompts rank lower in recruiter review.',
    cta: { label: 'Review prompts', path: '/prompts' },
  },
  {
    title: 'Add a clear profile photo and job title',
    body: 'Your photo and title appear on `/u/yourname` and in Message Hub threads. A professional headshot and accurate seniority help recruiters trust the profile at a glance.',
    cta: { label: 'Edit profile', path: '/user-profile' },
  },
  {
    title: 'Fill out your Portfolio tab',
    body: 'Projects, certifications, languages, and work-culture notes give depth beyond video answers. Recruiters often open Portfolio after watching your top prompts.',
    cta: { label: 'Build portfolio', path: '/create-portfolio' },
  },
  {
    title: 'Preview what recruiters see',
    body: 'Use Preview Mode on your dashboard to walk through Screening Prompts and Portfolio exactly as a recruiter would — no actions enabled, same layout as your shared link.',
    cta: { label: 'Open dashboard', path: '/dashboard' },
  },
  {
    title: 'Connect LinkedIn and professional links',
    body: 'LinkedIn and other links validate your background and unlock role-specific prompt suggestions tied to your job title and industry.',
    cta: { label: 'Update links', path: '/user-profile' },
  },
  {
    title: 'Share your calendar when ready',
    body: 'Once recruiters have profile access, an connected availability calendar removes scheduling back-and-forth. Keep slots current so booking feels effortless.',
    cta: { label: 'Calendar settings', path: '/calendar' },
  },
  {
    title: 'Reply quickly in Message Hub',
    body: 'Recruiters message candidates who are responsive. Check Message Hub regularly — timely replies keep you top of mind after a profile view.',
    cta: { label: 'Open Message Hub', path: '/messages' },
  },
  {
    title: 'Use Analytics to improve weak prompts',
    body: 'Views, impressions, likes, and per-prompt scores show which answers recruiters engage with. Polish low-scoring prompts with clearer video and supporting evidence.',
    cta: { label: 'View analytics', path: '/analytics' },
  },
];

type Props = {
  show: boolean;
  onHide: () => void;
};

export function ProfileTipsModal({ show, onHide }: Props) {
  const navigate = useNavigate();

  function goTo(path: string) {
    onHide();
    navigate(path);
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      size="lg"
      className="ss-profile-tips-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Build a stronger SkipScreen profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="ss-profile-tips-lede">
          SkipScreen lets recruiters evaluate you asynchronously — through video prompts, portfolio,
          and messaging — before they schedule time. These are the highest-impact steps candidates
          take to skip repetitive screening calls.
        </p>
        <ol className="ss-profile-tips-list">
          {PROFILE_TIPS.map((tip, index) => (
            <li key={tip.title} className="ss-profile-tips-item">
              <div className="ss-profile-tips-index">{index + 1}</div>
              <div className="ss-profile-tips-content">
                <h3 className="ss-profile-tips-title">{tip.title}</h3>
                <p className="ss-profile-tips-body">{tip.body}</p>
                {tip.cta ? (
                  <button
                    type="button"
                    className="ss-profile-tips-link"
                    onClick={() => goTo(tip.cta!.path)}
                  >
                    {tip.cta.label} →
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="success" onClick={() => goTo('/user-profile')}>
          Go to profile
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
